import { secureStorage, sanitizeInput, sanitizeErrorMessage } from '../utils/security';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';

/**
 * Dynamic Governance Configuration Service
 * Handles adaptive governance parameters based on token circulation and admin settings
 * 
 * FEATURES:
 * - Automatic threshold adjustment based on circulating supply
 * - Admin override capabilities for manual adjustments
 * - Historical parameter tracking
 * - Emergency governance modes
 */
class DynamicGovernanceConfig {
  constructor() {
    // Solana connection for blockchain signature verification
    this.connection = new Connection(
      process.env.REACT_APP_SOLANA_RPC_URL || 'https://api.devnet.solana.com',
      'confirmed'
    );

    // Multi-signature admin system - requires 3/3 confirmations
    this.ADMIN_WALLETS = [
      'HBSWmDcqtw63RZP4CtLSbJpDw5hsFrNFfB8Mf8YS6Qgz',
      'DV83NNLrrKqzWVdcN2AijShs8XHPJBf2jgjDbdPHJuTe', 
      '37sh7sMYurYHnxGPxWXwprS1tzt19zAJTdo5mvbo4Roi'
    ];

    // Multi-sig configuration
    this.MULTISIG_CONFIG = {
      REQUIRED_SIGNATURES: 3, // All 3 admins must confirm
      TOTAL_ADMINS: 3,
      PROPOSAL_EXPIRY: 24 * 60 * 60 * 1000, // 24 hours to collect signatures
      SIGNATURE_WINDOW: 7 * 24 * 60 * 60 * 1000 // 7 days max for proposal execution
    };

    // Base configuration percentages (of circulating supply)
    this.BASE_CONFIG = {
      // Percentage-based thresholds
      MIN_STAKE_TO_VOTE_PCT: 0.01,     // 0.01% of circulating supply
      MIN_STAKE_TO_PROPOSE_PCT: 0.05,  // 0.05% of circulating supply  
      QUORUM_THRESHOLD_PCT: 0.1,       // 0.1% of circulating supply
      VOTING_POWER_CAP_PCT: 2.0,       // 2% of circulating supply max per user
      
      // Absolute minimums (safety floor)
      ABSOLUTE_MIN_VOTE: 10,
      ABSOLUTE_MIN_PROPOSE: 50,
      ABSOLUTE_MIN_QUORUM: 100,
      ABSOLUTE_MAX_VOTING_POWER: 50000,
      
      // Absolute maximums (prevent excessive requirements)
      ABSOLUTE_MAX_VOTE: 1000,
      ABSOLUTE_MAX_PROPOSE: 5000,
      ABSOLUTE_MAX_QUORUM: 10000,
    };

    // Governance phases based on circulation
    this.GOVERNANCE_PHASES = {
      BOOTSTRAP: {
        name: 'Bootstrap Phase',
        description: 'Early launch with minimal requirements',
        circulationThreshold: 0,
        maxCirculation: 10000000, // 10M tokens
        multiplier: 0.5, // Reduce requirements by 50%
        features: ['emergency_admin_override', 'reduced_voting_period']
      },
      GROWTH: {
        name: 'Growth Phase', 
        description: 'Expanding community with moderate requirements',
        circulationThreshold: 10000000,
        maxCirculation: 100000000, // 100M tokens
        multiplier: 0.75, // Reduce requirements by 25%
        features: ['community_proposals', 'standard_voting']
      },
      MATURE: {
        name: 'Mature Phase',
        description: 'Established community with full requirements',
        circulationThreshold: 100000000,
        maxCirculation: 1000000000, // 1B tokens (max supply)
        multiplier: 1.0, // Full requirements
        features: ['full_governance', 'treasury_management', 'protocol_upgrades']
      }
    };

    this.loadConfiguration();
    this.initializeMultiSig();
  }

  /**
   * Initialize multi-signature system
   */
  initializeMultiSig() {
    // Load pending admin proposals
    this.pendingProposals = secureStorage.getItem('multisig_proposals') || {};
    this.executedProposals = secureStorage.getItem('executed_proposals') || [];
  }

  /**
   * Save multi-sig state
   */
  saveMultiSigState() {
    secureStorage.setItem('multisig_proposals', this.pendingProposals);
    secureStorage.setItem('executed_proposals', this.executedProposals);
  }

  /**
   * Load current configuration from storage
   */
  loadConfiguration() {
    const stored = secureStorage.getItem('governance_config');
    if (stored) {
      this.currentConfig = stored;
    } else {
      this.currentConfig = {
        lastUpdated: Date.now(),
        circulatingSupply: 5000000, // Default: 5M tokens in circulation
        currentPhase: 'BOOTSTRAP',
        adminOverrides: {},
        isEmergencyMode: false,
        configHistory: []
      };
      this.saveConfiguration();
    }
  }

  /**
   * Save configuration to storage
   */
  saveConfiguration() {
    secureStorage.setItem('governance_config', this.currentConfig);
  }

  /**
   * Create multi-sig proposal for admin action
   */
  async createMultiSigProposal(action, params, proposerWallet) {
    try {
      if (!this.isAdmin(proposerWallet)) {
        throw new Error('Unauthorized: Admin access required to create proposals');
      }

      const proposalId = `${action}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const proposal = {
        id: proposalId,
        action,
        params,
        proposer: proposerWallet,
        createdAt: Date.now(),
        expiresAt: Date.now() + this.MULTISIG_CONFIG.PROPOSAL_EXPIRY,
        signatures: [], // No auto-signing - all signatures must be blockchain-verified
        blockchainSignatures: [], // Track blockchain signatures separately
        executed: false,
        status: 'PENDING_SIGNATURES'
      };

      this.pendingProposals[proposalId] = proposal;
      this.saveMultiSigState();

      console.log(`📝 Multi-sig proposal created: ${proposalId} by ${proposerWallet}`);
      
      return {
        success: true,
        proposalId,
        requiredSignatures: this.MULTISIG_CONFIG.REQUIRED_SIGNATURES,
        currentSignatures: 0 // No auto-signatures - all must be blockchain-verified
      };

    } catch (error) {
      console.error('Error creating multi-sig proposal:', sanitizeErrorMessage(error));
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign a multi-sig proposal
   */
  async signMultiSigProposal(proposalId, signerWallet) {
    try {
      if (!this.isAdmin(signerWallet)) {
        throw new Error('Unauthorized: Admin access required to sign proposals');
      }

      const proposal = this.pendingProposals[proposalId];
      if (!proposal) {
        throw new Error('Proposal not found');
      }

      if (proposal.executed) {
        throw new Error('Proposal already executed');
      }

      if (Date.now() > proposal.expiresAt) {
        throw new Error('Proposal has expired');
      }

      if (proposal.signatures.includes(signerWallet)) {
        throw new Error('Admin has already signed this proposal');
      }

      // Add signature
      proposal.signatures.push(signerWallet);
      proposal.lastSignedAt = Date.now();

      // Check if we have enough signatures
      if (proposal.signatures.length >= this.MULTISIG_CONFIG.REQUIRED_SIGNATURES) {
        proposal.status = 'READY_FOR_EXECUTION';
        console.log(`✅ Multi-sig proposal ${proposalId} ready for execution`);
      }

      this.saveMultiSigState();

      return {
        success: true,
        proposalId,
        signatures: proposal.signatures.length,
        required: this.MULTISIG_CONFIG.REQUIRED_SIGNATURES,
        readyToExecute: proposal.status === 'READY_FOR_EXECUTION'
      };

    } catch (error) {
      console.error('Error signing proposal:', sanitizeErrorMessage(error));
      return { success: false, error: error.message };
    }
  }

  /**
   * Execute a fully signed multi-sig proposal
   */
  async executeMultiSigProposal(proposalId, executorWallet) {
    try {
      if (!this.isAdmin(executorWallet)) {
        throw new Error('Unauthorized: Admin access required to execute proposals');
      }

      const proposal = this.pendingProposals[proposalId];
      if (!proposal) {
        throw new Error('Proposal not found');
      }

      if (proposal.executed) {
        throw new Error('Proposal already executed');
      }

      // Check blockchain signatures instead of legacy signatures
      const blockchainSignatureCount = proposal.blockchainSignatures ? proposal.blockchainSignatures.length : 0;
      if (blockchainSignatureCount < this.MULTISIG_CONFIG.REQUIRED_SIGNATURES) {
        throw new Error(`Insufficient blockchain signatures: ${blockchainSignatureCount}/${this.MULTISIG_CONFIG.REQUIRED_SIGNATURES}`);
      }

      if (Date.now() > proposal.expiresAt + this.MULTISIG_CONFIG.SIGNATURE_WINDOW) {
        throw new Error('Proposal execution window has expired');
      }

      // Execute the action based on proposal type
      let result;
      switch (proposal.action) {
        case 'UPDATE_SUPPLY':
          result = await this._executeSupplyUpdate(proposal.params);
          break;
        case 'SET_OVERRIDE':
          result = await this._executeParameterOverride(proposal.params);
          break;
        case 'REMOVE_OVERRIDE':
          result = await this._executeRemoveOverride(proposal.params);
          break;
        case 'EMERGENCY_MODE':
          result = await this._executeEmergencyMode(proposal.params);
          break;
        case 'CANCEL_PROPOSAL':
          result = await this._executeCancelProposal(proposal.params);
          break;
        case 'RESET_STATE':
          result = await this._executeResetState(proposal.params);
          break;
        default:
          throw new Error(`Unknown proposal action: ${proposal.action}`);
      }

      if (result.success) {
        // Mark as executed
        proposal.executed = true;
        proposal.executedAt = Date.now();
        proposal.executedBy = executorWallet;
        proposal.status = 'EXECUTED';

        // Move to executed proposals
        this.executedProposals.push(proposal);
        delete this.pendingProposals[proposalId];

        this.saveMultiSigState();

        console.log(`🎉 Multi-sig proposal ${proposalId} executed successfully`);
        
        return {
          success: true,
          proposalId,
          result
        };
      } else {
        throw new Error(`Execution failed: ${result.error}`);
      }

    } catch (error) {
      console.error('Error executing proposal:', sanitizeErrorMessage(error));
      return { success: false, error: error.message };
    }
  }

  /**
   * Update circulating supply (now requires multi-sig)
   */
  async updateCirculatingSupply(newSupply, adminWallet = null) {
    if (!adminWallet) {
      // System update (automated) - no multi-sig required
      return this._executeSupplyUpdate({ newSupply });
    }

    // Admin update - requires multi-sig
    return this.createMultiSigProposal('UPDATE_SUPPLY', { newSupply }, adminWallet);
  }

  /**
   * Private method to execute supply update (called by multi-sig system)
   */
  async _executeSupplyUpdate(params) {
    try {
      const sanitizedSupply = parseInt(sanitizeInput(params.newSupply.toString()));
      
      if (sanitizedSupply < 0 || sanitizedSupply > 1000000000) {
        throw new Error('Invalid circulating supply value');
      }

      const oldSupply = this.currentConfig.circulatingSupply;
      const oldPhase = this.currentConfig.currentPhase;

      this.currentConfig.circulatingSupply = sanitizedSupply;
      this.currentConfig.currentPhase = this.determineGovernancePhase(sanitizedSupply);
      this.currentConfig.lastUpdated = Date.now();

      // Log configuration change
      this.currentConfig.configHistory.push({
        timestamp: Date.now(),
        action: 'SUPPLY_UPDATE',
        oldSupply,
        newSupply: sanitizedSupply,
        oldPhase,
        newPhase: this.currentConfig.currentPhase,
        updatedBy: 'MULTISIG_ADMIN'
      });

      this.saveConfiguration();

      console.log(`📊 Governance: Circulating supply updated from ${oldSupply} to ${sanitizedSupply}`);
      console.log(`🏛️ Governance phase: ${oldPhase} → ${this.currentConfig.currentPhase}`);

      return {
        success: true,
        oldSupply,
        newSupply: sanitizedSupply,
        phaseChange: oldPhase !== this.currentConfig.currentPhase
      };

    } catch (error) {
      console.error('Error executing supply update:', sanitizeErrorMessage(error));
      return { success: false, error: error.message };
    }
  }

  /**
   * Private method to execute parameter override (called by multi-sig system)
   */
  async _executeParameterOverride(params) {
    try {
      const { parameter, value } = params;
      const sanitizedValue = parseInt(sanitizeInput(value.toString()));
      
      if (!this.currentConfig.adminOverrides) {
        this.currentConfig.adminOverrides = {};
      }

      const oldValue = this.currentConfig.adminOverrides[parameter];
      this.currentConfig.adminOverrides[parameter] = sanitizedValue;

      // Log override
      this.currentConfig.configHistory.push({
        timestamp: Date.now(),
        action: 'ADMIN_OVERRIDE',
        parameter,
        oldValue,
        newValue: sanitizedValue,
        updatedBy: 'MULTISIG_ADMIN'
      });

      this.saveConfiguration();

      console.log(`🔧 Multi-sig override: ${parameter} = ${sanitizedValue}`);
      
      return { success: true, parameter, value: sanitizedValue };

    } catch (error) {
      console.error('Error executing parameter override:', sanitizeErrorMessage(error));
      return { success: false, error: error.message };
    }
  }

  /**
   * Private method to execute override removal (called by multi-sig system)
   */
  async _executeRemoveOverride(params) {
    try {
      const { parameter } = params;

      if (this.currentConfig.adminOverrides && this.currentConfig.adminOverrides[parameter]) {
        const oldValue = this.currentConfig.adminOverrides[parameter];
        delete this.currentConfig.adminOverrides[parameter];

        // Log removal
        this.currentConfig.configHistory.push({
          timestamp: Date.now(),
          action: 'REMOVE_OVERRIDE',
          parameter,
          oldValue,
          updatedBy: 'MULTISIG_ADMIN'
        });

        this.saveConfiguration();

        console.log(`🗑️ Multi-sig override removed: ${parameter}`);
        return { success: true, parameter };
      }

      return { success: false, error: 'Override not found' };

    } catch (error) {
      console.error('Error executing override removal:', sanitizeErrorMessage(error));
      return { success: false, error: error.message };
    }
  }

  /**
   * Private method to execute emergency mode toggle (called by multi-sig system)
   */
  async _executeEmergencyMode(params) {
    const { enable, reason } = params;
    
    if (enable) {
      this.currentConfig.isEmergencyMode = true;
      this.currentConfig.emergencyModeReason = reason;
      this.currentConfig.emergencyModeEnabledAt = Date.now();
      
      // Log the change
      this.currentConfig.configHistory.push({
        timestamp: Date.now(),
        action: 'EMERGENCY_MODE_ENABLED',
        reason: reason,
        adminWallet: 'MULTI_SIG_SYSTEM'
      });
      
      console.log(`🚨 Emergency mode ENABLED: ${reason}`);
      
    } else {
      this.currentConfig.isEmergencyMode = false;
      this.currentConfig.emergencyModeReason = null;
      this.currentConfig.emergencyModeDisabledAt = Date.now();
      
      // Log the change
      this.currentConfig.configHistory.push({
        timestamp: Date.now(),
        action: 'EMERGENCY_MODE_DISABLED',
        adminWallet: 'MULTI_SIG_SYSTEM'
      });
      
      console.log(`✅ Emergency mode DISABLED`);
    }
    
    // Save configuration
    this.saveConfiguration();
    
    return {
      success: true,
      isEmergencyMode: this.currentConfig.isEmergencyMode,
      reason: this.currentConfig.emergencyModeReason,
      message: enable ? 
        `Emergency mode enabled: ${reason}` : 
        'Emergency mode disabled'
    };
  }

  /**
   * Private method to execute proposal cancellation (called by multi-sig system)
   * SECURITY: Only executable after 3/3 multi-sig approval
   */
  async _executeCancelProposal(params) {
    const { targetProposalId, reason, targetProposalAction } = params;
    
    // Verify target proposal exists
    const targetProposal = this.pendingProposals[targetProposalId];
    if (!targetProposal) {
      throw new Error(`Target proposal ${targetProposalId} not found or already processed`);
    }
    
    // Remove the target proposal
    delete this.pendingProposals[targetProposalId];
    
    // Log the cancellation in executed proposals for audit trail
    this.executedProposals.push({
      id: `CANCEL_${targetProposalId}_${Date.now()}`,
      action: 'CANCEL_PROPOSAL',
      params: {
        canceledProposalId: targetProposalId,
        canceledProposalAction: targetProposalAction,
        reason
      },
      executedAt: Date.now(),
      executedBy: 'MULTI_SIG_SYSTEM',
      result: {
        success: true,
        canceledProposal: {
          id: targetProposalId,
          action: targetProposalAction,
          proposer: targetProposal.proposer,
          createdAt: targetProposal.createdAt
        }
      }
    });
    
    // Save state
    this.saveMultiSigState();
    
    console.log(`🚫 Proposal ${targetProposalId} (${targetProposalAction}) canceled via multi-sig: ${reason}`);
    
    return {
      success: true,
      canceledProposalId: targetProposalId,
      canceledProposalAction: targetProposalAction,
      reason,
      message: `Proposal ${targetProposalId} canceled: ${reason}`
    };
  }

  /**
   * Determine governance phase based on circulating supply
   */
  determineGovernancePhase(circulatingSupply) {
    for (const [phaseKey, phase] of Object.entries(this.GOVERNANCE_PHASES)) {
      if (circulatingSupply >= phase.circulationThreshold && 
          circulatingSupply <= phase.maxCirculation) {
        return phaseKey;
      }
    }
    return 'MATURE'; // Default to mature phase
  }

  /**
   * Get current voting configuration based on circulating supply and phase
   */
  getCurrentVotingConfig() {
    const phase = this.GOVERNANCE_PHASES[this.currentConfig.currentPhase];
    const supply = this.currentConfig.circulatingSupply;
    
    // Calculate percentage-based thresholds
    const baseVote = Math.max(
      Math.floor(supply * this.BASE_CONFIG.MIN_STAKE_TO_VOTE_PCT / 100),
      this.BASE_CONFIG.ABSOLUTE_MIN_VOTE
    );
    
    const basePropose = Math.max(
      Math.floor(supply * this.BASE_CONFIG.MIN_STAKE_TO_PROPOSE_PCT / 100),
      this.BASE_CONFIG.ABSOLUTE_MIN_PROPOSE
    );
    
    const baseQuorum = Math.max(
      Math.floor(supply * this.BASE_CONFIG.QUORUM_THRESHOLD_PCT / 100),
      this.BASE_CONFIG.ABSOLUTE_MIN_QUORUM
    );
    
    const baseVotingCap = Math.min(
      Math.floor(supply * this.BASE_CONFIG.VOTING_POWER_CAP_PCT / 100),
      this.BASE_CONFIG.ABSOLUTE_MAX_VOTING_POWER
    );

    // Apply phase multiplier
    const multiplier = phase.multiplier;
    
    let config = {
      MIN_STAKE_TO_VOTE: Math.min(
        Math.floor(baseVote * multiplier),
        this.BASE_CONFIG.ABSOLUTE_MAX_VOTE
      ),
      MIN_STAKE_TO_PROPOSE: Math.min(
        Math.floor(basePropose * multiplier),
        this.BASE_CONFIG.ABSOLUTE_MAX_PROPOSE
      ),
      QUORUM_THRESHOLD: Math.min(
        Math.floor(baseQuorum * multiplier),
        this.BASE_CONFIG.ABSOLUTE_MAX_QUORUM
      ),
      VOTING_POWER_CAP: baseVotingCap,
      VOTING_PERIOD: phase.name === 'BOOTSTRAP' ? 3 * 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000, // 3 days vs 7 days
      APPROVAL_THRESHOLD: 0.6, // Keep 60% approval constant
      
      // Metadata
      phase: phase.name,
      circulatingSupply: supply,
      lastUpdated: this.currentConfig.lastUpdated,
      features: phase.features
    };

    // Apply admin overrides
    if (this.currentConfig.adminOverrides) {
      config = { ...config, ...this.currentConfig.adminOverrides };
    }

    return config;
  }

  /**
   * Admin override for specific parameters (now requires multi-sig)
   */
  async setAdminOverride(parameter, value, adminWallet) {
    return this.createMultiSigProposal('SET_OVERRIDE', { parameter, value }, adminWallet);
  }

  /**
   * Remove admin override (now requires multi-sig)
   */
  async removeAdminOverride(parameter, adminWallet) {
    return this.createMultiSigProposal('REMOVE_OVERRIDE', { parameter }, adminWallet);
  }

  /**
   * Enable emergency governance mode (now requires multi-sig)
   */
  async enableEmergencyMode(adminWallet, reason) {
    return this.createMultiSigProposal('EMERGENCY_MODE', { enable: true, reason }, adminWallet);
  }

  /**
   * Disable emergency governance mode (now requires multi-sig)
   */
  async disableEmergencyMode(adminWallet) {
    return this.createMultiSigProposal('EMERGENCY_MODE', { enable: false }, adminWallet);
  }

  /**
   * Get pending multi-sig proposals
   */
  getPendingProposals() {
    const proposals = [];
    for (const [id, proposal] of Object.entries(this.pendingProposals)) {
      if (!proposal.executed && Date.now() <= proposal.expiresAt) {
        proposals.push({
          ...proposal,
          timeRemaining: proposal.expiresAt - Date.now(),
          signaturesNeeded: this.MULTISIG_CONFIG.REQUIRED_SIGNATURES - proposal.signatures.length
        });
      }
    }
    return proposals.sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Get executed proposals history
   */
  getExecutedProposals(limit = 20) {
    return this.executedProposals
      .slice(-limit)
      .reverse()
      .map(proposal => ({
        ...proposal,
        executedAt: new Date(proposal.executedAt).toISOString()
      }));
  }

  /**
   * Get multi-sig status for admin panel
   */
  getMultiSigStatus() {
    const pendingCount = Object.keys(this.pendingProposals).length;
    const executedCount = this.executedProposals.length;
    
    return {
      requiredSignatures: this.MULTISIG_CONFIG.REQUIRED_SIGNATURES,
      totalAdmins: this.MULTISIG_CONFIG.TOTAL_ADMINS,
      adminWallets: this.ADMIN_WALLETS,
      pendingProposals: pendingCount,
      executedProposals: executedCount,
      proposalExpiry: this.MULTISIG_CONFIG.PROPOSAL_EXPIRY / (60 * 60 * 1000), // hours
      signatureWindow: this.MULTISIG_CONFIG.SIGNATURE_WINDOW / (24 * 60 * 60 * 1000) // days
    };
  }

  /**
   * Create SOL transaction-based signature for a proposal
   * This creates a memo transaction on the blockchain as proof of signature
   */
  async createBlockchainSignature(proposalId, signerWallet, walletAdapter) {
    try {
      if (!this.isAdmin(signerWallet)) {
        throw new Error('Unauthorized: Only admin wallets can sign proposals');
      }

      const proposal = this.pendingProposals[proposalId];
      if (!proposal) {
        throw new Error('Proposal not found');
      }

      if (proposal.executed) {
        throw new Error('Proposal already executed');
      }

      if (Date.now() > proposal.expiresAt) {
        throw new Error('Proposal has expired');
      }

      // Check if already signed
      if (proposal.blockchainSignatures && proposal.blockchainSignatures.some(sig => sig.signer === signerWallet)) {
        throw new Error('Wallet has already signed this proposal');
      }

      // Create unique memo content with maximum randomness and timestamp precision
      const timestamp = Date.now();
      const microTimestamp = performance.now(); // High precision timestamp
      const randomNonce1 = Math.random().toString(36).substring(2, 15); // 13 characters
      const randomNonce2 = Math.random().toString(36).substring(2, 15); // 13 characters  
      const randomNonce3 = Math.random().toString(36).substring(2, 15); // 13 characters
      const randomNonce4 = Math.random().toString(36).substring(2, 15); // 13 characters
      const processId = Math.floor(Math.random() * 100000000); // 0-99,999,999
      const walletSubstring = signerWallet.substring(0, 12); // First 12 chars of wallet
      const sessionId = Math.floor(microTimestamp * 1000); // Microsecond precision
      const uniqueId = `${timestamp}_${sessionId}_${randomNonce1}_${randomNonce2}_${randomNonce3}_${randomNonce4}_${processId}_${walletSubstring}`;
      
      // Get recent blockhash and slot for additional uniqueness
      const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash();
      const blockhashFragment = blockhash.substring(0, 12);
      const slot = await this.connection.getSlot();
      
      const memoData = `ZNOOSPACE_GOVERNANCE_SIGN:${proposalId}:${signerWallet}:${uniqueId}:${slot}`;
      
      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: new PublicKey(signerWallet),
          toPubkey: new PublicKey(signerWallet), // Self-transfer of 0 SOL
          lamports: 0,
        })
      );

      // Add memo instruction with enhanced data
      const enhancedMemoData = `${memoData}:${blockhashFragment}`;
      const memoInstruction = {
        keys: [],
        programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
        data: Buffer.from(enhancedMemoData, 'utf8')
      };
      transaction.add(memoInstruction);

      // Set transaction properties
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = new PublicKey(signerWallet);
      
      // Add random delay to prevent rapid-fire transactions and ensure uniqueness
      const delayMs = Math.random() * 2000 + 1000; // 1-3 second delay
      await new Promise(resolve => setTimeout(resolve, delayMs));
      
      // Additional uniqueness check - get fresh blockhash after delay
      const freshBlockhash = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = freshBlockhash.blockhash;

      // Sign and send transaction with enhanced error handling
      let signedTransaction, signature;
      try {
        console.log('🔐 Requesting wallet signature for proposal:', proposalId);
        signedTransaction = await walletAdapter.signTransaction(transaction);
        console.log('✅ Transaction signed by wallet');
        
        signature = await this.connection.sendRawTransaction(signedTransaction.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed'
        });
        console.log('📡 Transaction sent to blockchain:', signature);
        
        // Wait for confirmation with timeout
        const confirmationPromise = this.connection.confirmTransaction(signature, 'confirmed');
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Transaction confirmation timeout')), 30000)
        );
        
        await Promise.race([confirmationPromise, timeoutPromise]);
        console.log(`✅ Blockchain signature confirmed: ${signature}`);
      } catch (error) {
        console.error('❌ Blockchain signature error:', error);
        if (error.message?.includes('Bf is not defined')) {
          throw new Error('Wallet adapter error - please refresh the page and try again');
        }
        throw error;
      }

      return {
        success: true,
        signature,
        memoData,
        blockNumber: await this.connection.getSlot(),
        timestamp: Date.now()
      };

    } catch (error) {
      console.error('Error creating blockchain signature:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Verify blockchain signature for a proposal
   */
  async verifyBlockchainSignature(proposalId, signerWallet, signature) {
    try {
      // Get transaction details
      const txDetails = await this.connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0
      });

      if (!txDetails) {
        return { valid: false, error: 'Transaction not found' };
      }

      // Check if transaction was successful
      if (txDetails.meta.err) {
        return { valid: false, error: 'Transaction failed' };
      }

      // Verify signer matches
      const txSigner = txDetails.transaction.message.accountKeys[0].toString();
      if (txSigner !== signerWallet) {
        return { valid: false, error: 'Signer mismatch' };
      }

      // Check for memo instruction with correct data
      const expectedMemo = `ZNOOSPACE_GOVERNANCE_SIGN:${proposalId}:${signerWallet}`;
      const memoFound = txDetails.meta.logMessages.some(log => 
        log.includes('Memo') && log.includes(expectedMemo)
      );

      if (!memoFound) {
        return { valid: false, error: 'Invalid memo data' };
      }

      return {
        valid: true,
        signature,
        blockTime: txDetails.blockTime,
        slot: txDetails.slot
      };

    } catch (error) {
      console.error('Error verifying blockchain signature:', error);
      return { valid: false, error: error.message };
    }
  }

  /**
   * Enhanced sign proposal with blockchain verification
   */
  async signProposalWithBlockchain(proposalId, signerWallet, walletAdapter) {
    try {
      // Create blockchain signature first
      const signatureResult = await this.createBlockchainSignature(proposalId, signerWallet, walletAdapter);
      
      if (!signatureResult.success) {
        return signatureResult;
      }

      // Verify the signature
      const verificationResult = await this.verifyBlockchainSignature(
        proposalId, 
        signerWallet, 
        signatureResult.signature
      );

      if (!verificationResult.valid) {
        return {
          success: false,
          error: `Signature verification failed: ${verificationResult.error}`
        };
      }

      // Update proposal with blockchain signature
      const proposal = this.pendingProposals[proposalId];
      
      // Initialize blockchain signatures array if not exists
      if (!proposal.blockchainSignatures) {
        proposal.blockchainSignatures = [];
      }

      // Add blockchain signature
      proposal.blockchainSignatures.push({
        signer: signerWallet,
        signature: signatureResult.signature,
        timestamp: signatureResult.timestamp,
        blockTime: verificationResult.blockTime,
        slot: verificationResult.slot,
        verified: true
      });

      // Also add to legacy signatures array for compatibility
      if (!proposal.signatures.includes(signerWallet)) {
        proposal.signatures.push(signerWallet);
      }

      // Check if proposal is ready for execution
      const isReady = proposal.blockchainSignatures.length >= this.MULTISIG_CONFIG.REQUIRED_SIGNATURES;
      if (isReady) {
        proposal.status = 'READY_FOR_EXECUTION';
      }

      // Save state
      this.saveMultiSigState();

      console.log(`✅ Proposal ${proposalId} signed on blockchain by ${signerWallet}`);
      console.log(`📊 Signatures: ${proposal.blockchainSignatures.length}/${this.MULTISIG_CONFIG.REQUIRED_SIGNATURES}`);

      return {
        success: true,
        signature: signatureResult.signature,
        signaturesCount: proposal.blockchainSignatures.length,
        requiredSignatures: this.MULTISIG_CONFIG.REQUIRED_SIGNATURES,
        readyForExecution: isReady
      };

    } catch (error) {
      console.error('Error signing proposal with blockchain:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }


  /**
   * Check if wallet is admin
   */
  isAdmin(walletAddress) {
    return this.ADMIN_WALLETS.includes(walletAddress);
  }

  /**
   * Get governance status summary
   */
  getGovernanceStatus() {
    const config = this.getCurrentVotingConfig();
    const phase = this.GOVERNANCE_PHASES[this.currentConfig.currentPhase];
    
    return {
      phase: phase.name,
      description: phase.description,
      circulatingSupply: this.currentConfig.circulatingSupply,
      isEmergencyMode: this.currentConfig.isEmergencyMode,
      thresholds: {
        minStakeToVote: config.MIN_STAKE_TO_VOTE,
        minStakeToPropose: config.MIN_STAKE_TO_PROPOSE,
        quorumThreshold: config.QUORUM_THRESHOLD,
        votingPowerCap: config.VOTING_POWER_CAP,
        votingPeriodDays: config.VOTING_PERIOD / (24 * 60 * 60 * 1000)
      },
      features: phase.features,
      lastUpdated: new Date(this.currentConfig.lastUpdated).toISOString(),
      adminOverrides: Object.keys(this.currentConfig.adminOverrides || {})
    };
  }

  /**
   * Get configuration history
   */
  getConfigHistory(limit = 20) {
    return this.currentConfig.configHistory
      .slice(-limit)
      .reverse()
      .map(entry => ({
        ...entry,
        timestamp: new Date(entry.timestamp).toISOString()
      }));
  }

  /**
   * Create multi-sig proposal to cancel a specific proposal
   * SECURITY: Requires 3/3 signatures to cancel any proposal
   */
  async createCancelProposal(targetProposalId, reason, adminWallet) {
    if (!this.isAdmin(adminWallet)) {
      throw new Error('Unauthorized: Only admin wallets can create cancel proposals');
    }

    const targetProposal = this.pendingProposals[targetProposalId];
    if (!targetProposal) {
      throw new Error(`Proposal ${targetProposalId} not found`);
    }

    return this.createMultiSigProposal('CANCEL_PROPOSAL', {
      targetProposalId,
      reason,
      targetProposalAction: targetProposal.action
    }, adminWallet);
  }

  /**
   * DEPRECATED: clearPendingProposals - SECURITY RISK
   * This method bypasses multi-signature security and should not be used in production.
   * Use createCancelProposal() instead for secure proposal cancellation.
   */
  clearPendingProposals(adminWallet) {
    // SECURITY WARNING: This method is deprecated due to security concerns
    console.error('🚨 SECURITY WARNING: clearPendingProposals() is deprecated and bypasses multi-sig security');
    console.error('🔒 Use createCancelProposal() instead for secure proposal cancellation');
    
    throw new Error('DEPRECATED: clearPendingProposals() is disabled for security. Use createCancelProposal() instead.');
  }

  /**
   * DEPRECATED: resetMultiSigState - SECURITY RISK
   * Single-admin state reset bypasses multi-signature security.
   * Use createResetProposal() instead for secure multi-sig reset.
   */
  resetMultiSigState(adminWallet) {
    console.error('🚨 SECURITY WARNING: resetMultiSigState() is deprecated and bypasses multi-sig security');
    console.error('🔒 Use createResetProposal() instead for secure multi-sig state reset');
    throw new Error('DEPRECATED: resetMultiSigState() is disabled for security. Use createResetProposal() instead.');
  }

  /**
   * Create multi-sig proposal to reset governance state
   * SECURITY: Requires 3/3 signatures to execute reset
   */
  async createResetProposal(reason, adminWallet) {
    if (!this.isAdmin(adminWallet)) {
      throw new Error('Unauthorized: Only admin wallets can create reset proposals');
    }

    return this.createMultiSigProposal('RESET_STATE', { reason }, adminWallet);
  }

  /**
   * Private method to execute state reset (called by multi-sig system only)
   */
  async _executeResetState(params) {
    const clearedProposals = Object.keys(this.pendingProposals).length;
    const clearedExecuted = this.executedProposals.length;

    this.pendingProposals = {};
    this.executedProposals = [];
    this.saveMultiSigState();

    console.log(`🔄 Multi-sig reset: ${clearedProposals} pending, ${clearedExecuted} executed proposals cleared. Reason: ${params.reason}`);
    return {
      success: true,
      clearedProposals,
      clearedExecuted,
      reason: params.reason,
      message: `Multi-sig reset complete: ${clearedProposals} pending and ${clearedExecuted} executed proposals cleared`
    };
  }
}

// Export singleton instance
export default new DynamicGovernanceConfig();
